import { datadogRum } from "@datadog/browser-rum";
import uniqBy from "lodash/uniqBy";
import { ChoosenKeyword } from "PFApp/search/search_box/choosen_keywords";
import { useSearchStoreContext } from "PFApp/search/search_context";
import { Button } from "PFComponents/button";
import { PureCustomValueData } from "PFTypes";
import { useTranslation } from "react-i18next";

import { Keyword } from "./keyword";
import css from "./smart_suggestions_list.module.scss";

const PAGE_SIZE = 6;

type SuggestionItem = PureCustomValueData;

type SmartSuggestionsListProps = {
  items: SuggestionItem[];
  title?: string;
  onAdd: (item: SuggestionItem) => void;
  onRemove?: (item: SuggestionItem) => void;
  selected?: (item: SuggestionItem) => boolean;
  cycle?: () => void;
  cycleOffset?: number;
  style?: object;
  usedOn: string;
  userId: number;
};

export const SmartSuggestionsList = ({
  items,
  title,
  onAdd,
  onRemove,
  selected,
  cycle,
  cycleOffset,
  style,
  usedOn,
  userId
}: SmartSuggestionsListProps) => {
  const {
    search: { term }
  } = useSearchStoreContext();

  const letCycle = cycle && items.length > PAGE_SIZE;

  const safeItems = uniqBy(items, "value");

  let shownItems;
  if (letCycle) {
    const offset = ((cycleOffset || 0) % Math.ceil(safeItems.length / PAGE_SIZE)) * PAGE_SIZE;
    shownItems = safeItems.slice(offset, offset + PAGE_SIZE);
    if (shownItems.length < PAGE_SIZE) {
      shownItems = safeItems.slice(-PAGE_SIZE);
    }
  } else {
    shownItems = safeItems.slice(0, PAGE_SIZE);
  }

  const pushToDatadog = (item, action) =>
    datadogRum.addAction(`${usedOn}-recommendations`, {
      userId,
      action,
      searchText: term,
      recommendations: shownItems,
      selectedRecommendation: item.value,
      index: shownItems.findIndex(({ value }) => value === item.value)
    });

  const handleRemove = (item) => {
    onRemove?.(item);
    pushToDatadog(item, "decline");
  };

  const handleAdd = (item) => {
    onAdd(item);
    pushToDatadog(item, "accept");
  };

  return (
    shownItems.length > 0 && (
      <div className={css.list} style={style} data-qa-id="SmartSuggestionsList">
        {title && <span className={css.listTitle}>{title}</span>}
        <div>
          {shownItems.map((item) =>
            selected && selected(item) ? (
              <ChoosenKeyword key={item.value} text={item.value} onRemove={() => handleRemove(item)} />
            ) : (
              // only user can assign this weight
              <Keyword
                key={item.value}
                text={item.value}
                onAdd={() => handleAdd({ ...item, importance: null })}
              />
            )
          )}
        </div>
        {letCycle && <RefreshLink cycle={cycle} />}
      </div>
    )
  );
};

type RefreshLinkProps = {
  cycle: () => void;
};

const RefreshLink = ({ cycle }: RefreshLinkProps) => {
  const { t } = useTranslation("search");

  const handleRefresh = () => {
    cycle();
  };

  return (
    <Button
      kind="text"
      onClick={handleRefresh}
      icon="refresh"
      text={t("smartSuggestions.refreshSuggestions")}
    />
  );
};
